import type {ClientTool, ClientToolCall} from '@github-ui/ahp-relay/relay-connection-runtime'
import {createHandoffStorage, type HandoffStorage} from '@github-ui/ahp-session-client/handoff-storage'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {fetchCAPI, FetchCAPIError} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {ToolResultContentType, ToolCallResult} from '@microsoft/agent-host-protocol'

export interface PullRequestConfirmation {
  repoNwo: string
  title: string
  body: string
  head: string
  base: string
}

export interface PullRequestToolContext {
  repoNwo: string
  taskId: string
  sessionId: string
  environmentId: string
}

interface PullIdentity {
  id: number
  number: number
  repository_id: number
}

export type PullRequestCreationRecord =
  | {phase: 'invoked'}
  | {phase: 'creating'}
  | {phase: 'created'; pull: PullIdentity}

interface Dependencies {
  context: PullRequestToolContext
  apiURL?: string
  origin?: string
  storage?: Pick<HandoffStorage<PullRequestCreationRecord>, 'read' | 'write' | 'replace'>
}

const pendingCreations = new Map<string, Promise<ToolCallResult>>()
const successfulCreations = new Map<string, PullRequestCreationRecord>()
// The exact parsed input (`{title, body, head, base}`, stringified) that produced each key's successful
// creation, so a later call with genuinely different arguments can be told apart from a recovery replay
// of the same call. Only covers a live tab — a reload loses this map along with `invokedKeys`, so recovery
// after a reload falls back to the lenient "trust the cached result" behavior below.
const originalInputs = new Map<string, string>()
// Once invoked (success or failure), every later call for the same context is refused by `fulfil` — the in-memory set covers a live tab, the sessionStorage marker it writes covers a reload.
const invokedKeys = new Set<string>()
const INVOKED_RECORD_ID = 'invoked'
const RECOVERY_SLOT_ID = 'pr'

function toolKey(context: PullRequestToolContext): string {
  const {repoNwo, taskId, sessionId, environmentId} = context
  return JSON.stringify([getCurrentUserLogin(), repoNwo, taskId, sessionId, environmentId])
}

export const CREATE_PULL_REQUEST_TOOL_DEFINITION: ClientTool['definition'] = {
  name: 'create_pull_request',
  title: 'Create a draft pull request',
  description:
    'Create a draft pull request for the current repository session. First prepare the changes and push the ' +
    'head branch to this repository. Do not invoke this tool until the push succeeds. The user is asked to ' +
    'confirm before the draft is created. Only repository-scoped CMC sessions support this prototype. ' +
    'This tool can only be used once per task session: on the first attempt, do not create the pull request ' +
    'through another tool. If it refuses because it was already used, fall back to another method instead ' +
    '(for example pushing the branch and running `gh pr create`).',
  inputSchema: {
    type: 'object',
    properties: {
      title: {type: 'string', description: 'Pull request title.'},
      body: {type: 'string', description: 'Pull request description.'},
      head: {type: 'string', description: 'Published head branch in the current repository, without an owner prefix.'},
      base: {type: 'string', description: 'Target branch in the current repository.'},
    },
    required: ['title', 'body', 'head', 'base'],
  },
  annotations: {readOnlyHint: false, destructiveHint: false, idempotentHint: false, openWorldHint: true},
  _meta: {'github.com/copilot:safeForTelemetry': {name: true, inputsNames: false}},
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function isPullIdentity(value: unknown): value is PullIdentity {
  return (
    isObject(value) &&
    [value.id, value.number, value.repository_id].every(
      field => typeof field === 'number' && Number.isSafeInteger(field) && field > 0,
    )
  )
}

function isCreationRecord(value: unknown): value is PullRequestCreationRecord {
  return (
    isObject(value) &&
    (value.phase === 'invoked' ||
      value.phase === 'creating' ||
      (value.phase === 'created' && isPullIdentity(value.pull)))
  )
}

function isBranch(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value !== '@' &&
    !/[\s~^:?*[\]\\]/.test(value) &&
    [...value].every(character => character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127) &&
    !value.includes('..') &&
    !value.includes('@{') &&
    !value.startsWith('-') &&
    !value.startsWith('refs/') &&
    !value.endsWith('.') &&
    value.split('/').every(part => part.length > 0 && !part.startsWith('.') && !part.endsWith('.lock'))
  )
}

function parseInput(call: ClientToolCall, repoNwo: string): PullRequestConfirmation {
  const input: unknown = JSON.parse(call.toolInput ?? '{}')
  if (
    !isObject(input) ||
    typeof input.title !== 'string' ||
    !input.title.trim() ||
    input.title.length > 256 ||
    typeof input.body !== 'string' ||
    input.body.length > 65_536 ||
    !isBranch(input.head) ||
    !isBranch(input.base) ||
    input.head === input.base ||
    Object.keys(input).some(key => !['title', 'body', 'head', 'base'].includes(key))
  ) {
    throw new Error('Provide a title, body, and different head and base branch names in the current repository.')
  }
  return {repoNwo, title: input.title.trim(), body: input.body, head: input.head, base: input.base}
}

/** Like {@link parseInput}, but returns `undefined` instead of throwing, so callers can tell "could not
 * prove a difference" apart from "the inputs differ" without conflating parse failures with real errors. */
function tryParseInput(call: ClientToolCall, repoNwo: string): string | undefined {
  try {
    return JSON.stringify(parseInput(call, repoNwo))
  } catch {
    return undefined
  }
}

function assertWellFormedContext(context: PullRequestToolContext): void {
  if (
    !/^[a-zA-Z0-9-]+\/[a-zA-Z0-9_.-]+$/.test(context.repoNwo) ||
    context.repoNwo.split('/').some(part => part === '.' || part === '..') ||
    !context.taskId ||
    !context.sessionId ||
    !context.environmentId
  ) {
    throw new Error('Pull request creation requires a well-formed repository, task, session and environment identity.')
  }
}

function completedResult(record: PullRequestCreationRecord, deps: Dependencies): ToolCallResult {
  if (record.phase !== 'created') throw new Error('The saved pull request identity is invalid.')
  const pull = record.pull
  const url = new URL(`/${deps.context.repoNwo}/pull/${pull.number}`, deps.origin ?? window.location.origin).href
  return {
    success: true,
    pastTenseMessage: 'Created a draft pull request',
    structuredContent: {url, id: pull.id, number: pull.number, draft: true},
    content: [{type: 'text' as ToolResultContentType.Text, text: `Created draft pull request: ${url}`}],
  }
}

function recordId(value: PullRequestCreationRecord): string {
  return value.phase === 'invoked' ? INVOKED_RECORD_ID : RECOVERY_SLOT_ID
}

function defaultStorage(key: string): HandoffStorage<PullRequestCreationRecord> {
  return createHandoffStorage<PullRequestCreationRecord>({
    key: `github.create-pull-request.${key}`,
    payloadChars: () => 512,
    validate: isCreationRecord,
    getId: recordId,
  })
}

/**
 * Read-only check for UI display purposes only (e.g. disabling a stale retry trigger after the
 * tool has already been used once). This does NOT enforce anything — `fulfil` above is the sole
 * authority for refusing reuse; this is just a best-effort hint for the UI to avoid offering a
 * dead-end retry.
 */
export function isCreatePullRequestConsumed(context: PullRequestToolContext): boolean {
  const key = toolKey(context)
  if (invokedKeys.has(key)) return true
  const reader = createHandoffStorage<PullRequestCreationRecord>({
    key: `github.create-pull-request.${key}`,
    payloadChars: () => 512,
    validate: isCreationRecord,
  })
  return reader.read() !== null
}

export function buildCreatePullRequestClientTool(deps: Dependencies): ClientTool {
  const key = toolKey(deps.context)
  const storage = deps.storage ?? defaultStorage(key)
  return {
    definition: CREATE_PULL_REQUEST_TOOL_DEFINITION,
    fulfil: call => {
      // Emitted here, before the pending-call guard below, so every external call attempt counts —
      // including ones deduped into a shared in-flight promise, which never reach the internal `fulfil` below.
      sendEvent('copilot.agent_session.pr_creation_tool_invoked', {
        nwo: deps.context.repoNwo,
        task_id: deps.context.taskId,
      })
      // Sampled before this call's own marking below, so it only reflects an *earlier* call.
      const alreadyInvoked = invokedKeys.has(key) || storage.read() !== null
      invokedKeys.add(key)
      // Best-effort: only mark "invoked" if nothing is stored yet, so this never clobbers a `creating`/`created` record from an earlier call.
      if (!storage.read()) storage.write({phase: 'invoked'})
      const pending = pendingCreations.get(key)
      if (pending) return pending
      const result = fulfil(call, deps, key, storage, alreadyInvoked).finally(() => pendingCreations.delete(key))
      pendingCreations.set(key, result)
      return result
    },
  }
}

const ALREADY_CALLED_MESSAGE =
  'This tool was already called once for this task session and cannot be used again. ' +
  'Create the pull request a different way instead (for example by pushing the branch and running `gh pr create`).'

function describeStepFailure(step: string, error: unknown): string {
  if (!(error instanceof FetchCAPIError)) return error instanceof Error ? error.message : String(error)
  switch (error.status) {
    case 400:
      return `Could not ${step}: the task or session is not ready for this yet.`
    case 403:
      return `Could not ${step}: push access to this repository is required.`
    case 404:
      return `Could not ${step}: the task, session, or repository could not be found.`
    case 409:
      return `Could not ${step}: a pull request may already exist for this task.`
    case 422:
      return `Could not ${step}: the request was rejected.`
    default:
      return `Could not ${step}.`
  }
}

// These are the only PR-creation statuses that mean the backend outright rejected the request —
// nothing was created. Every other failure (network errors, 5xx, timeouts) is ambiguous, so the
// "may have succeeded" recovery marker must stay in place for those.
const DEFINITE_PR_CREATION_REJECTION_STATUSES = new Set([400, 404, 409, 422])

async function fulfil(
  call: ClientToolCall,
  deps: Dependencies,
  key: string,
  storage: Pick<HandoffStorage<PullRequestCreationRecord>, 'read' | 'write' | 'replace'>,
  alreadyInvoked: boolean,
): Promise<ToolCallResult> {
  let persisted = false
  let creationStarted = false
  let step = 'update the session with pull request details'
  try {
    const saved = successfulCreations.get(key) ?? storage.read()
    if (saved?.phase === 'created') {
      // Only refuse a differing call if we can actually prove it differs: a missing stored original
      // (e.g. after a reload, when only sessionStorage — not this in-memory map — survives) or a new
      // call whose input doesn't even parse can't be compared, so both fall back to the lenient,
      // pre-existing "trust the cached result" recovery behavior. A narrow gap remains: two truly
      // concurrent calls with different input can still be deduped into one result by
      // `pendingCreations` below before either is persisted; that's an accepted, documented edge case.
      const storedInput = originalInputs.get(key)
      const newInput = storedInput === undefined ? undefined : tryParseInput(call, deps.context.repoNwo)
      if (storedInput !== undefined && newInput !== undefined && newInput !== storedInput) {
        throw new Error(ALREADY_CALLED_MESSAGE)
      }
      return completedResult(saved, deps)
    }
    if (saved?.phase === 'creating') {
      throw new Error(
        "A previous creation may have succeeded. Check the task's pull request before trying again; do not retry automatically.",
      )
    }
    // `saved` can only be undefined or `{phase: 'invoked'}` here; this prototype doesn't support retrying a failed attempt or creating a second PR.
    if (alreadyInvoked) {
      throw new Error(ALREADY_CALLED_MESSAGE)
    }
    const {context} = deps
    assertWellFormedContext(context)
    const input = parseInput(call, context.repoNwo)
    // This only confirms the identity's shape, not that `sessionId` is the session the PR-creation POST
    // below will actually use: CMC's `CreatePullRequestForTask` picks the task's most-recently-created
    // session rather than accepting a sessionId, so the PUT and POST can race across two live sessions on
    // the same task. No local check can close this; it needs a backend change (e.g. an explicit sessionId
    // parameter on PR creation), which is out of scope here.
    await fetchCAPI({
      path: `/agents/sessions/${encodeURIComponent(context.sessionId)}`,
      copilotApiUrl: deps.apiURL,
      method: 'PUT',
      body: {
        head_ref: input.head,
        base_ref: input.base,
        generated_pull_data: {title: input.title, description: input.body},
      },
    })
    persisted = true
    // `replace` rather than `write`: `write` clears the existing record before setting the new one, so
    // a `setItem` failure in between (e.g. a quota error) would silently drop the `invoked` marker it
    // just removed. `replace` only ever issues a single `setItem`, so a failure here leaves the prior
    // `invoked` marker intact instead of losing the single-use guard on reload.
    const storageFailure = storage.replace(INVOKED_RECORD_ID, {phase: 'creating'})
    if (storageFailure)
      throw new Error(`Cannot preserve PR creation recovery state (${storageFailure}); creation did not start.`)
    creationStarted = true
    step = 'create the pull request'
    sendEvent('copilot.agent_session.pr_creation_requested', {nwo: context.repoNwo, task_id: context.taskId})
    const response = await fetchCAPI({
      path: `/agents/repos/${context.repoNwo}/tasks/${encodeURIComponent(context.taskId)}/pulls`,
      copilotApiUrl: deps.apiURL,
      method: 'POST',
      retryOn401: false,
    })
    const pull: unknown = await response.json()
    if (!isPullIdentity(pull)) {
      throw new Error('Creation returned an invalid pull request identity. Check the task before trying again.')
    }
    const record: PullRequestCreationRecord = {phase: 'created', pull}
    successfulCreations.set(key, record)
    originalInputs.set(key, JSON.stringify(input))
    const saveFailure = storage.replace(RECOVERY_SLOT_ID, record)
    const result = completedResult(record, deps)
    sendEvent('copilot.agent_session.pr_creation_completed', {
      nwo: context.repoNwo,
      task_id: context.taskId,
      pr_id: pull.id,
      pr_number: pull.number,
    })
    if (saveFailure) {
      result.content?.push({
        type: 'text' as ToolResultContentType.Text,
        text: `The PR exists, but the browser could not save its recovery result (${saveFailure}). Keep the URL; do not create another PR after reloading.`,
      })
    }
    return result
  } catch (error) {
    // `rawMessage` can carry untrusted text from an upstream HTTP response (FetchCAPIError's
    // message is parsed from the response body) — never surface that to the agent as tool
    // output (tool output poisoning). Our own thrown errors are static, developer-authored
    // strings with no interpolated untrusted content, so they stay safe to show verbatim.
    const rawMessage = error instanceof Error ? error.message : String(error)
    const message = describeStepFailure(step, error)
    if (creationStarted) {
      sendEvent('copilot.agent_session.pr_creation_failed', {
        nwo: deps.context.repoNwo,
        task_id: deps.context.taskId,
        status: error instanceof FetchCAPIError ? error.status : undefined,
        error: rawMessage,
      })
    }
    // A definite rejection (the backend outright refused the PR-creation request) means nothing was
    // created, so the "creating" marker left by the attempt above would otherwise strand every later
    // call behind the ambiguous "may have succeeded" refusal forever. Revert it back to "invoked" so a
    // later call instead falls through to the ordinary single-use guard. If reverting fails, don't
    // compound the error — fall back to leaving the ambiguous marker in place.
    let definitelyNotCreated = false
    if (
      creationStarted &&
      error instanceof FetchCAPIError &&
      DEFINITE_PR_CREATION_REJECTION_STATUSES.has(error.status)
    ) {
      const clearFailure = storage.replace(RECOVERY_SLOT_ID, {phase: 'invoked'})
      if (!clearFailure) {
        definitelyNotCreated = true
      } else {
        // eslint-disable-next-line no-console
        console.warn(
          `Could not clear the PR creation recovery marker (${clearFailure}); leaving the ambiguous "may have succeeded" state in place.`,
        )
      }
    }
    const outcome = definitelyNotCreated
      ? 'No pull request was created.'
      : creationStarted
        ? 'CMC saved the PR metadata and creation may have succeeded. Do not retry automatically.'
        : persisted
          ? 'CMC saved the PR metadata, but PR creation did not start.'
          : 'No PR creation request was sent.'
    return {
      success: false,
      pastTenseMessage: 'Could not create a draft pull request',
      error: {message},
      content: [{type: 'text' as ToolResultContentType.Text, text: `${message} ${outcome}`}],
    }
  }
}
